import React from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import iconDeliveryTruck from "../../../../img/icons/delivery-truck.svg";
import iconQuestionMarkCircleFilled from "../../../../img/icons/question-mark-circle-filled.svg";
import { UncontrolledPopover } from "../../../common/Popover";
import { RichText } from "../../../common/RichText";
import { ConfiguratorTypes } from "../../../constants";
import { IProduct } from "../../../models/catalogue";
import { BreakPoint } from "../../../models/screen";
import { FreeDeliveryButtonTypes } from "../constants";
import { IModularConfiguratorShippingMessage } from "../models";

import styles from "./ConfiguratorFreeDelivery.module.scss";

const handleAfterModalOpen = (contentListRef: HTMLUListElement | null) => {
    // In the modal, adding a modifier to center the vertical lines between lists
    if (contentListRef) {
        Array.from(contentListRef.children, (elem) => {
            if (elem.clientHeight >= 45) {
                elem.classList.add("wider");
            }
        });
    }
};

const getFreeDeliveryText = (
    freeDeliveryButtonTypes: FreeDeliveryButtonTypes,
    variantIsAvailableToBuy: boolean | undefined,
    upgradedVariant: IProduct | null | undefined,
    freeDeliveryElemRef: React.RefObject<HTMLDivElement | null>,
): React.ReactNode => {
    if (
        freeDeliveryButtonTypes === FreeDeliveryButtonTypes.TEXT &&
        variantIsAvailableToBuy &&
        upgradedVariant &&
        upgradedVariant.value_props
    ) {
        // TODO(i18n): Hardcoded string comparison - "Free Delivery" should be compared using a non-translatable key or translated string
        const shippingProp = upgradedVariant.value_props.find(
            (vp) => vp.name === "Free Delivery",
        );
        if (shippingProp) {
            return (
                <div ref={freeDeliveryElemRef}>
                    <SVG
                        className="mysvg"
                        aria-hidden="true"
                        src={iconDeliveryTruck}
                    />
                    {t`FREE Delivery`}{" "}
                </div>
            );
        }
    }
    return null;
};

const getFreeDeliveryIcon = (
    freeDeliveryButtonTypes: FreeDeliveryButtonTypes,
): React.ReactNode => {
    if (freeDeliveryButtonTypes !== FreeDeliveryButtonTypes.ICON) {
        return null;
    }
    return <SVG aria-hidden="true" src={iconQuestionMarkCircleFilled} />;
};

const buildShippingMessages = (
    shippingMessages: IModularConfiguratorShippingMessage | null | undefined,
): React.ReactNode => {
    if (!shippingMessages?.messages) {
        return null;
    }
    const shippingMessage = shippingMessages.messages.map(
        (message: string, index: number) => {
            return (
                <li key={index}>
                    <figure>{index + 1}</figure>
                    <p>{message}</p>
                </li>
            );
        },
    );

    return shippingMessage;
};

export const ConfiguratorFreeDelivery = (props: {
    upgradedVariant?: IProduct | null;
    variantIsAvailableToBuy?: boolean;
    freeDeliveryButtonTypes: FreeDeliveryButtonTypes;
    phoneNumber: string | undefined;
    configuratorType: ConfiguratorTypes;
    shippingMessages?: IModularConfiguratorShippingMessage | null;
}): React.ReactNode => {
    const freeDeliveryElemRef = React.useRef<HTMLDivElement>(null);
    const contentListRef = React.useRef<HTMLUListElement>(null);

    React.useEffect(() => {
        // Put setTimeout to ensures that the browser DOM has been loaded before executing the animation.
        // There might be a better way but not feagured out yet.
        setTimeout(() => {
            if (!freeDeliveryElemRef.current) {
                return;
            }
            freeDeliveryElemRef.current.classList.add("animate");
        }, 0);
    }, []);

    const onAfterModalOpen = () => {
        handleAfterModalOpen(contentListRef.current);
    };

    if (
        props.configuratorType !== ConfiguratorTypes.PANEL &&
        props.configuratorType !== ConfiguratorTypes.UPDATED
    ) {
        return null;
    }

    const pNumber =
        props.phoneNumber && props.phoneNumber.replace(/[^A-Z0-9]/gi, "");

    return (
        <UncontrolledPopover
            triggerContent={
                <>
                    {getFreeDeliveryText(
                        props.freeDeliveryButtonTypes,
                        props.variantIsAvailableToBuy,
                        props.upgradedVariant,
                        freeDeliveryElemRef,
                    )}
                    {getFreeDeliveryIcon(props.freeDeliveryButtonTypes)}
                </>
            }
            popperOptions={{
                modifiers: [
                    {
                        name: "offset",
                        options: {
                            offset: [0, -18],
                        },
                    },
                ],
                placement: "left",
            }}
            modalProps={{
                overlayClassName: `${styles.root}`,
                className: `${styles.freeDeliveryModal}`,
                contentLabel: t`Hassle-Free Delivery info`,
            }}
            showArrow={true}
            isStandardModalBelowSize={BreakPoint.MEDIUM}
            onAfterOpen={onAfterModalOpen}
        >
            <div className={styles.freeDeliveryModalContent}>
                {props.shippingMessages?.header ? (
                    <RichText
                        className={styles.freeDeliveryModalContentHeader}
                        html={props.shippingMessages?.header}
                    />
                ) : (
                    <h5>{t`Hassle-Free Delivery`}</h5>
                )}
                <ul ref={contentListRef}>
                    {buildShippingMessages(props.shippingMessages)}
                </ul>
                {props.shippingMessages?.footer ? (
                    <RichText
                        className={styles.freeDeliveryModalContentFooter}
                        html={props.shippingMessages?.footer}
                    />
                ) : (
                    <>
                        {/* TODO(i18n): Split sentence - combine into single translatable string with interpolated phone number and link using jt */}
                        <p>
                            {t`Need assistance with your order? Call us at`}
                            <br />
                            <a href={`tel:1${pNumber}`}>{props.phoneNumber}</a>
                        </p>
                        <p>
                            {t`and we'll do our best to accommodate your request.`}
                        </p>
                    </>
                )}
            </div>
        </UncontrolledPopover>
    );
};
